import { useMemo, useState } from 'react'
import { Icon } from '@carrent/ui'
import { boundsOf, clusterPoints, positioned } from './cluster'

/**
 * Карта автопарка.
 *
 * Второй режим просмотра того же среза: фильтры, сигналы и поиск общие
 * с таблицей. Карта не показывает свой набор машин — она показывает
 * тот же, что и список, иначе два режима начали бы противоречить
 * друг другу.
 *
 * Подложки нет сознательно: см. комментарий в cluster.js. Клик по
 * кластеру углубляет масштаб до его границ, клик по одиночной метке
 * открывает панель.
 */

const WIDTH = 960
const HEIGHT = 460

export function FleetMap({ cars, onSelect }) {
  const [zoomTo, setZoomTo] = useState(null)

  const points = useMemo(() => positioned(cars), [cars])
  const withoutPosition = cars.length - points.length

  const bounds = useMemo(() => zoomTo || boundsOf(points), [zoomTo, points])

  const clusters = useMemo(
    () => clusterPoints(points, { width: WIDTH, height: HEIGHT, bounds }),
    [points, bounds],
  )

  if (points.length === 0) {
    return (
      <div className="state">
        <Icon name="map" size={32} />
        <p className="state__title">Показывать на карте нечего</p>
        <p className="state__text">
          {cars.length === 0
            ? 'В этом срезе машин нет.'
            : 'Ни у одной машины в срезе нет свежих координат — они появляются во время поездки.'}
        </p>
      </div>
    )
  }

  const openCluster = (cluster) => {
    if (cluster.count === 1) {
      onSelect(cluster.items[0].car)
      return
    }
    setZoomTo(boundsOf(cluster.items))
  }

  return (
    <div className="fleet-map">
      <div className="fleet-map__bar">
        <span className="fleet-map__hint">
          {zoomTo ? 'Показана часть парка' : 'Показан весь срез'}
          {withoutPosition > 0 && ` · без координат: ${withoutPosition}`}
        </span>
        {zoomTo && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setZoomTo(null)}>
            <Icon name="expand" size={14} />
            Весь срез
          </button>
        )}
      </div>

      <div
        className="fleet-map__canvas"
        style={{ aspectRatio: `${WIDTH} / ${HEIGHT}` }}
        role="group"
        aria-label="Карта автопарка"
      >
        {clusters.map((cluster) => (
          <button
            key={cluster.key}
            type="button"
            className={`fleet-map__marker ${
              cluster.count > 1 ? 'fleet-map__marker--cluster' : ''
            }`.trim()}
            style={{
              left: `${(cluster.left / WIDTH) * 100}%`,
              top: `${(cluster.top / HEIGHT) * 100}%`,
            }}
            onClick={() => openCluster(cluster)}
            title={
              cluster.count === 1
                ? `${cluster.items[0].car.plate} — ${cluster.items[0].car.brand} ${cluster.items[0].car.model}`
                : `Машин в группе: ${cluster.count}`
            }
          >
            {cluster.count > 1 ? cluster.count : <Icon name="car" size={14} />}
          </button>
        ))}
      </div>
    </div>
  )
}
